import { useState, useEffect } from 'react';
import { useNavigate }         from 'react-router-dom';
import { useAuth }             from '../context/AuthContext';
import { authFetch }           from '../utils/api';

const STEPS = {
  pending_qb:      1,
  pending_payment: 2,
  active:          3,
};

export default function Onboarding() {
  const { token, user }          = useAuth();
  const navigate                 = useNavigate();
  const [status,   setStatus]    = useState(null);
  const [loading,  setLoading]   = useState(true);
  const [paying,   setPaying]    = useState(false);
  const [error,    setError]     = useState(null);

  // Load onboarding status
  useEffect(() => {
    authFetch('/onboard/status')
      .then(r => r.json())
      .then(data => {
        setStatus(data);
        setLoading(false);
        // Already complete — go straight to dashboard
        if (data.onboarding_complete) navigate('/');
      })
      .catch(() => setLoading(false));
  }, [navigate]);

  // Connect QuickBooks
  function connectQB() {
    window.location.href = `/auth/qb?token=${token}`;
  }

  // Start Stripe checkout
  async function startCheckout() {
    setPaying(true);
    setError(null);
    try {
      const res  = await authFetch('/stripe/create-checkout', { method: 'POST' });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Checkout failed');
      }
    } catch (err) {
      setError(err.message);
      setPaying(false);
    }
  }

  if (loading) return (
    <div style={{
      minHeight:      '100vh',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      backgroundColor:'#0C1A36',
      color:          '#EDF1FF',
      fontFamily:     'Sora, sans-serif',
    }}>
      Loading your account...
    </div>
  );

  const currentStep = STEPS[status?.current_step] || 1;

  const stepLabels = ['Create Account', 'Connect QB', 'Subscribe'];

  return (
    <div style={{
      minHeight:      '100vh',
      backgroundColor:'#0C1A36',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      fontFamily:     'Sora, sans-serif',
      padding:        '24px',
    }}>
      <div style={{
        backgroundColor: '#112240',
        borderRadius:    '16px',
        padding:         '48px',
        width:           '100%',
        maxWidth:        '520px',
        boxShadow:       '0 20px 60px rgba(0,0,0,0.4)',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>
            <span style={{ color: '#1A7FBE' }}>Acct</span>
            <span style={{ color: '#F5C800' }}>4</span>
            <span style={{ color: '#3DAA4A' }}>Cast</span>
          </div>
          <p style={{ color: '#7A90B8', fontSize: '0.875rem', marginTop: '4px' }}>
            Let's get your dashboard ready
          </p>
        </div>

        {/* Step progress bar */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            {stepLabels.map((label, i) => {
              const stepNum  = i + 1;
              const done     = stepNum < currentStep;
              const active   = stepNum === currentStep;
              return (
                <div key={label} style={{
                  display:    'flex',
                  flexDirection:'column',
                  alignItems: 'center',
                  gap:        '6px',
                  flex:       1,
                }}>
                  <div style={{
                    width:        '32px',
                    height:       '32px',
                    borderRadius: '50%',
                    background:   done
                      ? '#3DAA4A'
                      : active
                        ? 'linear-gradient(135deg,#00A8CC,#3DAA4A)'
                        : '#1E3A5F',
                    display:      'flex',
                    alignItems:   'center',
                    justifyContent:'center',
                    color:        '#fff',
                    fontSize:     '0.875rem',
                    fontWeight:   700,
                    border:       active ? '2px solid #00A8CC' : 'none',
                  }}>
                    {done ? '✓' : stepNum}
                  </div>
                  <span style={{
                    fontSize:  '0.7rem',
                    color:     active ? '#EDF1FF' : done ? '#3DAA4A' : '#4A5568',
                    fontWeight:active ? 600 : 400,
                    textAlign: 'center',
                  }}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Progress line */}
          <div style={{ position: 'relative', height: '3px', background: '#1E3A5F', borderRadius: '2px' }}>
            <div style={{
              position:     'absolute',
              left:         0,
              top:          0,
              height:       '100%',
              borderRadius: '2px',
              background:   'linear-gradient(90deg,#00A8CC,#3DAA4A)',
              width:        `${((currentStep - 1) / 2) * 100}%`,
              transition:   'width 0.5s ease',
            }} />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background:   '#2D1B1B',
            border:       '1px solid #C0223A',
            borderRadius: '8px',
            padding:      '12px 16px',
            marginBottom: '24px',
            color:        '#FF6B6B',
            fontSize:     '0.875rem',
          }}>
            {error}
          </div>
        )}

        {/* Step 1 — Connect QuickBooks */}
        {status?.current_step === 'pending_qb' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔗</div>
            <h2 style={{ color: '#EDF1FF', fontSize: '1.4rem', fontWeight: 700, marginBottom: '12px' }}>
              Connect Your QuickBooks
            </h2>
            <p style={{ color: '#7A90B8', lineHeight: 1.6, marginBottom: '32px', fontSize: '0.95rem' }}>
              Acct4Cast reads your real financial data directly from QuickBooks Online.
              Your data stays in QuickBooks — we just read it.
            </p>
            <div style={{
              background:   '#0C1A36',
              borderRadius: '8px',
              padding:      '16px 20px',
              marginBottom: '28px',
              textAlign:    'left',
            }}>
              {[
                'Live Budget vs. Actual reports',
                'Automatic variance alerts',
                'Full-year Rate of Climb forecast',
                'What-if scenario sandbox',
              ].map(item => (
                <div key={item} style={{
                  display:    'flex',
                  alignItems: 'center',
                  gap:        '10px',
                  padding:    '6px 0',
                  color:      '#EDF1FF',
                  fontSize:   '0.875rem',
                }}>
                  <span style={{ color: '#3DAA4A', fontWeight: 700 }}>✓</span>
                  {item}
                </div>
              ))}
            </div>
            <button
              onClick={connectQB}
              style={{
                width:        '100%',
                padding:      '16px',
                background:   'linear-gradient(135deg,#00A8CC,#3DAA4A)',
                border:       'none',
                borderRadius: '8px',
                color:        '#fff',
                fontSize:     '1rem',
                fontWeight:   700,
                cursor:       'pointer',
              }}
            >
              Connect QuickBooks →
            </button>
            <p style={{ color: '#4A5568', fontSize: '0.75rem', marginTop: '12px' }}>
              You'll be redirected to Intuit to authorize access
            </p>
          </div>
        )}

        {/* Step 2 — Subscribe */}
        {status?.current_step === 'pending_payment' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
            <h2 style={{ color: '#EDF1FF', fontSize: '1.4rem', fontWeight: 700, marginBottom: '8px' }}>
              QuickBooks Connected
            </h2>
            <p style={{ color: '#3DAA4A', fontSize: '0.875rem', marginBottom: '28px' }}>
              Your QuickBooks account is linked. One step left.
            </p>

            {/* Pricing card */}
            <div style={{
              background:   '#0C1A36',
              borderRadius: '12px',
              padding:      '28px',
              marginBottom: '24px',
              border:       '1px solid #1E3A5F',
            }}>
              <div style={{ color: '#7A90B8', fontSize: '0.8rem', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Acct4Cast Pro
              </div>
              <div style={{ color: '#EDF1FF', fontSize: '2.5rem', fontWeight: 800, lineHeight: 1 }}>
                $49
                <span style={{ fontSize: '1rem', fontWeight: 400, color: '#7A90B8' }}>/month</span>
              </div>
              <div style={{ color: '#7A90B8', fontSize: '0.8rem', marginTop: '4px', marginBottom: '20px' }}>
                Cancel anytime · No contracts
              </div>
              {[
                'Unlimited Budget vs. Actual reports',
                'Real-time variance alerts',
                'AI-powered financial insights',
                'What-if scenario sandbox',
                'Email report delivery',
              ].map(item => (
                <div key={item} style={{
                  display:    'flex',
                  alignItems: 'center',
                  gap:        '10px',
                  padding:    '5px 0',
                  color:      '#EDF1FF',
                  fontSize:   '0.85rem',
                  textAlign:  'left',
                }}>
                  <span style={{ color: '#3DAA4A' }}>✓</span>
                  {item}
                </div>
              ))}
            </div>

            <button
              onClick={startCheckout}
              disabled={paying}
              style={{
                width:        '100%',
                padding:      '16px',
                background:   'linear-gradient(135deg,#00A8CC,#3DAA4A)',
                border:       'none',
                borderRadius: '8px',
                color:        '#fff',
                fontSize:     '1rem',
                fontWeight:   700,
                cursor:       paying ? 'not-allowed' : 'pointer',
                opacity:      paying ? 0.7 : 1,
              }}
            >
              {paying ? 'Redirecting to checkout...' : 'Start Subscription — $49/month →'}
            </button>
            <p style={{ color: '#4A5568', fontSize: '0.75rem', marginTop: '12px' }}>
              Secured by Stripe · Credit card required
            </p>
          </div>
        )}

        {/* Step 3 — Complete */}
        {status?.current_step === 'active' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎉</div>
            <h2 style={{ color: '#EDF1FF', fontSize: '1.4rem', fontWeight: 700, marginBottom: '12px' }}>
              You're all set!
            </h2>
            <p style={{ color: '#7A90B8', lineHeight: 1.6, marginBottom: '32px' }}>
              Your dashboard is ready. Your QuickBooks data is connected and your subscription is active.
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                width:        '100%',
                padding:      '16px',
                background:   'linear-gradient(135deg,#00A8CC,#3DAA4A)',
                border:       'none',
                borderRadius: '8px',
                color:        '#fff',
                fontSize:     '1rem',
                fontWeight:   700,
                cursor:       'pointer',
              }}
            >
              Go to Dashboard →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}